import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AUTO_CENTER_PAUSE_MS,
  autoCenterTop,
  avgPrice,
  bracketPrices,
  bracketTag,
  resolvePreset,
  centeredTop,
  domClickOrder,
  dragChange,
  edgeScroll,
  isCancellable,
  isModifiable,
  isVisible,
  ladderRows,
  notActionableReason,
  openQty,
  ownOrdersByPrice,
  scrollTop,
  useSettings,
  useTrading,
  wheelRows,
  type BracketPreset,
  type BracketSide,
  type ExactPosition,
  type OrderEvent,
  type OwnOrders,
  type TradeSide,
} from "@helm/core";
import {
  domPnlCell,
  domPnlUnitOf,
  formatAvgPrice,
  formatCents,
  formatPerContractTicks,
  formatTicks,
  formatVolume,
  type DomPnlCell,
  type DomPnlUnit,
  mdStateLine,
  type PriceFormat,
} from "@helm/ui";
import { useMarket, useMdStatus } from "../md/marketData";
import { useInDock } from "../workspace/dockContext";
import { StaleBadge } from "../md/StaleBadge";
import { settingsSync } from "../session";
import { Icon } from "../components/Icon";
import { QtyStepper } from "../components/QtyStepper";
import { pushToast } from "../components/Toasts";
import { useOrderEntry } from "../trading/useOrderEntry";
import { useHotkeyTarget } from "../trading/hotkeys";
import { anchorOf, type Anchor } from "../trading/hosts";
import { cancelOrder, confirmStopMove, exitAction, modifyOrder } from "../trading/actions";
import { SimBadge } from "../trading/SimBadge";
import { ReduceOnlyNotice } from "../trading/ReduceOnlyNotice";
import { AccountSelect } from "../trading/AccountSelect";
import { OneClickToggle } from "../trading/OneClickToggle";
import { QtyLimitBadge } from "../trading/QtyLimitBadge";
import { useOpenPosition, useTradingViewSlice } from "../viewmodel/account";
import { BracketSelect, useSelectedPreset } from "../brackets/BracketSelect";
import { contractFacts } from "../brackets/bracketStore";

/** Row height before the first row is measured (CSS `.dom-row > span` is 20 px). */
const DEFAULT_ROW_PX = 20;
/** Ms between edge-scroll steps while an order is dragged near the top / bottom. */
const EDGE_SCROLL_MS = 60;

const CLICK_SEMANTICS =
  "Click a Bid cell for a buy limit at that price, an Ask cell for a sell limit; Shift+click or right-click for a stop";
const MY_SEMANTICS =
  "Your working orders at this price: click (or ×) to cancel, drag to another price to move them, right-click for more";

/** Hover text for one P/L cell: both units, so the toggle never hides information. */
function pnlTitle(cell: DomPnlCell, px: number, fmt: PriceFormat, netQty: number): string {
  const money = cell.cents === null ? "tick value unknown" : formatCents(cell.cents, { signed: true });
  const ticks = formatPerContractTicks(cell.tickContracts, netQty);
  const qty = Math.abs(netQty);
  return `If price reaches ${formatTicks(px, fmt)}: ${money} (${ticks} ticks × ${String(qty)} contract${qty === 1 ? "" : "s"})${cell.isAvg ? " · average entry row" : ""}`;
}

/** Row price under a pointer, in whichever window the ladder lives in (pop-outs included). */
function priceAt(doc: Document, x: number, y: number): number | null {
  const row = doc.elementFromPoint(x, y)?.closest("[data-price]");
  const v = row?.getAttribute("data-price");
  return v === null || v === undefined ? null : Number(v);
}

type Drag = { side: TradeSide; orders: OrderEvent[]; from: number; over: number };

/** `col`: the side column the order would rest in (buys on the bid side, sells on the ask side). */
type GhostRow = { kind: "tp" | "sl" | "entry"; label: string; col: "bid" | "ask" };

/**
 * Ghost bracket preview (SPEC §9.4, owner 2026-09-27 "like Tradovate"): where the selected
 * preset's targets and stops would rest for a market entry at the touch (buy at the ask, sell at
 * the bid). The tag sits in the column of the order's side, like a working order; legs sharing a
 * price share a row ("TP1+2"). Display only. `preset` is already resolved for this contract (a
 * points / $ template's ticks re-derived — ADR-0049); a leg without a target or stop has no row.
 */
function ghostRows(g: DomGhost, bid: number | null, ask: number | null): Map<number, GhostRow> {
  const out = new Map<number, GhostRow>();
  const entry = g.side === "buy" ? ask : bid;
  if (entry === null) return out;
  const legs = bracketPrices(g.preset, g.side, entry);
  const multi = legs.length > 1;
  const add = (px: number, kind: GhostRow["kind"], label: string): void => {
    const col = (kind === "entry") === (g.side === "buy") ? "bid" : "ask";
    const cur = out.get(px);
    if (!cur) out.set(px, { kind, label, col });
    else if (cur.kind === kind) out.set(px, { ...cur, label: `${cur.label}+${label.replace(/^\D+/, "")}` });
  };
  add(entry, "entry", g.side === "buy" ? "Buy" : "Sell");
  legs.forEach((l, i) => {
    if (l.targetTicks !== null) add(l.targetTicks, "tp", multi ? `TP${String(i + 1)}` : "TP");
    if (l.stopTicks !== null) add(l.stopTicks, "sl", multi ? `SL${String(i + 1)}` : "SL");
  });
  return out;
}

export type DomGhost = { preset: BracketPreset; side: BracketSide };

function GhostTag({ g }: { g: GhostRow }): React.JSX.Element {
  return <span className={`dom-ghost-tag dom-ghost-tag-${g.kind}`}>{g.label}</span>;
}

/**
 * What a row's cells do, stable for the ladder's life (they call the ladder's latest handlers), so
 * the memoized rows re-render only when what they show changes — not on every quote.
 */
type RowActions = {
  click: (column: "bid" | "ask", px: number, stop: boolean, ev: React.MouseEvent) => void;
  drag: (side: TradeSide, list: OrderEvent[], from: number, ev: React.PointerEvent<HTMLElement>) => void;
  cancel: (list: readonly OrderEvent[]) => void;
  move: (list: readonly OrderEvent[], to: number) => void;
  menu: (m: OrderMenu) => void;
};

/** Right-click menu on your orders (cancel one price, one side, or every order on the contract). */
type OrderMenu = { x: number; y: number; doc: Document; side: TradeSide; px: number; list: OrderEvent[] };

/**
 * DOM ladder (Tradesea columns: My Bid · Bid · Price · Ask · My Ask · P&L · Vol; SPEC §13.1). Fed by
 * live BBO and prints from the main-thread md client. Full depth (BookUpdate) and the worker-drawn
 * ladder arrive in Phase 4.
 *
 * **Free scrolling** (owner 2026-09-28 "needs to go up and down as much as I need to set take
 * profit and stop loss"): the ladder is virtual — only the rows that fit are rendered, from `top`
 * (integer ticks) downwards — so the wheel, PageUp / PageDown, ↑ / ↓ and dragging an order to an
 * edge reach any price with no fixed window. Auto-centre (SPEC §13.1: recentre when the last price
 * leaves the middle 60 %, paused 5 s after a manual scroll) is a toggle, synced as
 * `dom_auto_center`; Center / Space recentre by hand, and an off-screen last price shows a pill.
 *
 * Click trading (owner 2026-09-27, ADR-0021): a Bid cell = buy limit at that price, an Ask cell =
 * sell limit; Shift+click or right-click = stop (Tradovate). My Bid / My Ask show this account's
 * working orders at each price (only once the gateway's OrderEvent says they work — nothing
 * optimistic): click the cell or its × to cancel, drag to another row to move (ModifyOrder on
 * release), right-click for Cancel at this price / Cxl all buys / sells / all on the contract; ↑ /
 * ↓ on a focused order moves it a tick, Delete cancels. "Cxl" in the My Bid / My Ask headers cancels
 * that side. Cancels are never gated by the trading switch or risk (ADR-0027). The header's
 * 1-CLICK / CONFIRM badge turns the confirmation popover off / on (§9.8, owner 2026-09-28).
 *
 * Brackets (ADR-0025): with a bracket preset selected for the symbol (the order pad's picker), a
 * click that opens or adds to a position goes out with that bracket; the gateway's stop and target
 * then show in My Bid / My Ask like any working order, tagged TP1 / SL1 … (dragging a stop moves
 * it; the bracket keeps the new level).
 *
 * P&L column (owner request, Tradovate-style): with an open position in the selected account and
 * this contract, each row shows the P&L the position would have at that price (exact integer
 * math, `domPnlCell`); the average-entry row is marked. Blank when flat. The header toggles
 * dollars ↔ ticks, persisted as `dom_pnl_unit` in the synced settings.
 */
export function DomLadder({
  symbol,
  compact = false,
  qty: padQty,
  ghost = null,
}: {
  symbol: string | null;
  compact?: boolean;
  /** Order quantity from the surrounding order pad; a standalone ladder has its own stepper. */
  qty?: number;
  /** Preview the selected bracket preset's TP/SL rows (order pad DOM mode). */
  ghost?: DomGhost | null;
}): React.JSX.Element {
  const preset = useSelectedPreset(symbol);
  const md = useMarket(symbol);
  const facts = contractFacts(symbol, md?.info);
  const e = useOrderEntry(symbol, "DOM", { bracket: preset, facts });
  const m = e.m;
  const [ownQty, setOwnQty] = useState(1);
  const qty = padQty ?? ownQty;
  const status = useMdStatus();
  const fmt = e.fmt;
  const anchor = m?.lastTicks ?? (m?.bidTicks !== null && m?.bidTicks !== undefined ? m.bidTicks : null);
  const autoCenter = useSettings((s) => s.settings.dom_auto_center);
  const inDock = useInDock();
  /** Price (ticks) of the first visible row; null until the first price. */
  const [top, setTop] = useState<number | null>(null);
  const [count, setCount] = useState(41);
  const [rowPx, setRowPx] = useState(DEFAULT_ROW_PX);
  const pausedUntil = useRef(0);
  const anchorRef = useRef(anchor);
  anchorRef.current = anchor;
  const measuredOnce = useRef(false);
  const wheelCarry = useRef(0);
  const bodyRef = useRef<HTMLDivElement>(null);
  // Shown for the account the ladder views (the main one while All accounts is selected, as
  // before); trading itself needs a specific account (the gate says so). The position without its
  // mark: the P&L column depends on the position, not on the quote.
  const activeId = useTradingViewSlice((v) => v.activeId, Object.is);
  const pos = useOpenPosition(activeId, e.contract);
  const unit = domPnlUnitOf(useSettings((s) => s.settings.dom_pnl_unit));
  const tickValue = m?.info?.tickValueCents ?? 0;
  const orders = useTrading((s) => s.orders);
  const mine = useMemo(
    () => ownOrdersByPrice(orders.values(), activeId, e.contract),
    [orders, activeId, e.contract],
  );
  const [drag, setDrag] = useState<Drag | null>(null);
  const [menu, setMenu] = useState<OrderMenu | null>(null);
  const ctxId = useHotkeyTarget((action, el) => {
    e.hotkey(action, qty, el);
  });

  /** Scroll by whole rows; pauses auto-centre (SPEC §13.1: 5 s ⚙). */
  const scrollBy = useCallback((rows: number): void => {
    if (rows === 0) return;
    pausedUntil.current = Date.now() + AUTO_CENTER_PAUSE_MS;
    setTop((t) => (t === null ? t : scrollTop(t, rows)));
  }, []);

  // How many rows fit (and their height), also when the panel is resized.
  const hasBody = symbol !== null && top !== null && fmt !== null;
  useLayoutEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const measure = (): void => {
      const h = body.querySelector<HTMLElement>(".dom-row")?.offsetHeight ?? DEFAULT_ROW_PX;
      const px = h > 0 ? h : DEFAULT_ROW_PX;
      const n = Math.max(1, Math.ceil(body.clientHeight / px));
      setRowPx(px);
      setCount(n);
      // The first real measurement centres the last price in the rows that actually fit.
      const a = anchorRef.current;
      if (!measuredOnce.current && a !== null) {
        measuredOnce.current = true;
        setTop(centeredTop(a, n));
      }
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(body);
    return () => {
      ro.disconnect();
    };
  }, [hasBody]);

  // First price centres the ladder; afterwards auto-centre (when on and not paused) recentres
  // only when the last price leaves the middle 60 %.
  useEffect(() => {
    if (anchor === null) return;
    setTop((t) => {
      if (t === null) return centeredTop(anchor, count);
      if (!autoCenter || Date.now() < pausedUntil.current) return t;
      return autoCenterTop(t, count, anchor);
    });
  }, [anchor, count, autoCenter]);
  useEffect(() => {
    setTop(null);
    measuredOnce.current = false;
  }, [symbol]);

  // The wheel scrolls the ladder (a non-passive listener, so the page never scrolls instead).
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const onWheel = (ev: WheelEvent): void => {
      ev.preventDefault();
      const r = wheelRows(ev.deltaY, ev.deltaMode, rowPx, count, wheelCarry.current);
      wheelCarry.current = r.carry;
      scrollBy(r.rows);
    };
    body.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      body.removeEventListener("wheel", onWheel);
    };
  }, [hasBody, rowPx, count, scrollBy]);

  const maxVol = useMemo(() => {
    let v = 0;
    for (const x of m?.volumeAtPrice.values() ?? []) v = Math.max(v, x);
    return v;
  }, [m?.volumeAtPrice]);

  const recenter = (): void => {
    pausedUntil.current = 0;
    if (anchor !== null) setTop(centeredTop(anchor, count));
  };

  /** A click in the Bid / Ask column (SPEC §13.1, Tradovate): limit, or stop with Shift / right-click. */
  const clickCell = (column: "bid" | "ask", px: number, stop: boolean, ev: React.MouseEvent): void => {
    const side: TradeSide = column === "bid" ? "buy" : "sell";
    const title = `${side === "buy" ? "Buy" : "Sell"} ${stop ? "stop" : "limit"} @ ${formatTicks(px, fmt)}`;
    const g = e.placeGate(side, qty, true, px);
    if (!g.ok) {
      pushToast({ tone: "warn", title, body: g.reason }, { ttlMs: 5000 });
      return;
    }
    const o = domClickOrder({ column, priceTicks: px, stop, bestBid: e.bid, bestAsk: e.ask });
    if (!o.ok) {
      pushToast({ tone: "warn", title, body: o.reason }, { ttlMs: 5000 });
      return;
    }
    e.place(
      `dom:${o.side}:${o.type}:${String(px)}`,
      o.type === "limit"
        ? { side: o.side, type: "limit", qty, limitTicks: o.priceTicks }
        : { side: o.side, type: "stop", qty, stopTicks: o.priceTicks },
      anchorOf(ev),
    );
  };

  /**
   * Cancel your orders at one price (owner 2026-09-28 "DOM doesn't allow me to cancel pending
   * orders"): each through `CancelOrder`, one per order, never gated by the switch or risk. An
   * order that cannot be cancelled yet says why instead of doing nothing.
   */
  const cancelMine = (list: readonly OrderEvent[]): void => {
    const why = notActionableReason(list);
    if (why !== null) {
      pushToast({ tone: "warn", title: "Cancel", body: why }, { ttlMs: 5000 });
      return;
    }
    for (const o of list.filter(isCancellable)) {
      void cancelOrder(
        `${e.scope}:cxl:${o.clientOrderId}`,
        o.accountId,
        o.clientOrderId,
        `Cancel ${o.symbol} order`,
      );
    }
  };
  /** Cxl per side / contract (SPEC §9.7 Cxl Bids / Cxl Offers / Cxl Orders), one gateway action. */
  const cancelSide = (which: "buy" | "sell" | "all"): void => {
    const c = e.contract;
    const kind =
      which === "buy" ? "CANCEL_BIDS" : which === "sell" ? "CANCEL_OFFERS" : "CANCEL_SYMBOL_ORDERS";
    const words = which === "buy" ? "buy" : which === "sell" ? "sell" : "all";
    void exitAction(
      `${e.scope}:cxl-${which}`,
      kind,
      e.accountId,
      c,
      `Cancel ${words} orders · ${c ?? ""}`.trim(),
    );
  };
  const moveMine = (list: readonly OrderEvent[], to: number, anchor: Anchor | null = null): void => {
    void (async () => {
      for (const o of list.filter(isModifiable)) {
        const change = dragChange(o, to);
        if (change === null) continue;
        // A protective stop dragged through the market exits at once: asked first (AB-3).
        if (!(await confirmStopMove(o, change.stopTicks, (t) => formatTicks(t, fmt), anchor))) continue;
        void modifyOrder(
          `${e.scope}:mod:${o.clientOrderId}`,
          o.accountId,
          o.clientOrderId,
          change,
          `Move ${o.symbol} order to ${formatTicks(to, fmt)}`,
        );
      }
    })();
  };

  const startDrag = (
    side: TradeSide,
    list: OrderEvent[],
    from: number,
    ev: React.PointerEvent<HTMLElement>,
  ): void => {
    if (ev.button !== 0 || list.length === 0) return;
    ev.preventDefault();
    const doc = ev.currentTarget.ownerDocument;
    setDrag({ side, orders: list, from, over: from });
    let lastEdge = 0;
    let lastY = ev.clientY;
    const edge = (): void => {
      // Dragging a stop or target to a far price: the ladder scrolls under the pointer at an edge.
      const body = bodyRef.current;
      if (!body) return;
      const r = body.getBoundingClientRect();
      const dir = edgeScroll(lastY, r.top, r.bottom, rowPx);
      const now = Date.now();
      if (dir !== 0 && now - lastEdge >= EDGE_SCROLL_MS) {
        lastEdge = now;
        scrollBy(dir);
      }
    };
    const timer = setInterval(edge, EDGE_SCROLL_MS);
    const move = (me: PointerEvent): void => {
      lastY = me.clientY;
      const px = priceAt(doc, me.clientX, me.clientY);
      if (px !== null) setDrag((d) => (d && d.over !== px ? { ...d, over: px } : d));
    };
    const up = (ue: PointerEvent): void => {
      clearInterval(timer);
      doc.removeEventListener("pointermove", move);
      doc.removeEventListener("pointerup", up);
      const to = priceAt(doc, ue.clientX, ue.clientY) ?? from;
      setDrag(null);
      // Released on its own row: a click, which cancels. Elsewhere: move (314 on release, §9.3).
      if (to === from) cancelMine(list);
      else moveMine(list, to, { x: ue.clientX, y: ue.clientY, doc });
    };
    doc.addEventListener("pointermove", move);
    doc.addEventListener("pointerup", up);
  };

  const latest = useRef({ clickCell, startDrag, cancelMine, moveMine });
  latest.current = { clickCell, startDrag, cancelMine, moveMine };
  const actions = useMemo<RowActions>(
    () => ({
      click: (column, px, stop, ev) => {
        latest.current.clickCell(column, px, stop, ev);
      },
      drag: (side, list, from, ev) => {
        latest.current.startDrag(side, list, from, ev);
      },
      cancel: (list) => {
        latest.current.cancelMine(list);
      },
      move: (list, to) => {
        latest.current.moveMine(list, to);
      },
      menu: setMenu,
    }),
    [],
  );

  const rows = top === null ? [] : ladderRows(top, count);
  const cellGate = e.placeGate("buy", qty, true, e.bid);
  const clickTip = cellGate.ok ? CLICK_SEMANTICS : `${CLICK_SEMANTICS} — ${cellGate.reason}`;
  // Cancels are account-level actions: never gated by the trading switch or risk (ADR-0027).
  const cancelGate = e.exitGate("cancelAll");
  const cancelBlock = cancelGate.ok ? null : cancelGate.reason;
  const bidT = m?.bidTicks ?? null;
  const askT = m?.askTicks ?? null;
  // The ghost shows what would be sent here: a points / $ template re-derived for this contract;
  // nothing when it cannot be sized (the pad says why).
  const ghostPreset = ghost ? resolvePreset(ghost.preset, facts) : null;
  const ghostResolved = ghostPreset?.ok ? ghostPreset.preset : null;
  const ghostSide = ghost?.side ?? null;
  const ghosts = useMemo(
    () =>
      ghostResolved && ghostSide ? ghostRows({ preset: ghostResolved, side: ghostSide }, bidT, askT) : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the resolved ticks, not the object
    [JSON.stringify(ghostResolved?.legs), ghostSide, bidT, askT],
  );
  const hasBuys = [...mine.values()].some((o) => o.buy.length > 0);
  const hasSells = [...mine.values()].some((o) => o.sell.length > 0);
  const lastOff =
    top !== null && anchor !== null && !isVisible(anchor, top, count) ? (anchor > top ? "up" : "down") : null;
  // The same levels as text, top price first: rows beyond the visible window still read here.
  const ghostStrip =
    ghosts && ghosts.size > 0 && fmt ? (
      <div className="dom-ghost-strip num" aria-label="Bracket preview levels">
        {[...ghosts.entries()]
          .sort((a, b) => b[0] - a[0])
          .map(([px, g]) => (
            <span key={px} className={`dom-ghost-lvl dom-ghost-lvl-${g.kind}`}>
              {g.label} {formatTicks(px, fmt)}
            </span>
          ))}
      </div>
    ) : null;
  // The instrument is the module header's picker (owner 2026-10-04: shown once per module); the
  // resolved contract and tick size stay in this row's tooltip.
  const instrument = `${m?.info?.symbol ?? symbol ?? "—"}${fmt ? ` · tick ${(fmt.tickSizeNum / fmt.tickSizeDen).toString()}` : ""}`;
  const header = (
    <div className="dom-head" title={instrument}>
      {/* The standalone ladder's account leads its order row (the pad's DOM mode has the pad's). */}
      {!compact && <AccountSelect />}
      {!inDock && <span className="dom-symbol">{m?.info?.symbol ?? symbol ?? "—"}</span>}
      <StaleBadge symbol={symbol} />
      {pos && (
        <span
          className={`dom-pos num ${pos.netQty > 0 ? "tone-pos" : "tone-neg"}`}
          title={`Open position in ${pos.accountId}${pos.exactCost ? "" : " (average rounded by the gateway)"}`}
        >
          {pos.netQty > 0 ? "+" : "−"}
          {Math.abs(pos.netQty)} @ {formatAvgPrice(avgPrice(pos.position), fmt)}
        </span>
      )}
      <span className="spacer" />
      {!compact && (
        // The standalone ladder picks its own bracket template (the pad's DOM mode has the pad's).
        <span className="dom-bracket">
          <BracketSelect symbol={symbol} facts={facts} qty={qty} compact label="TP/SL:" />
        </span>
      )}
      {/* Trading state and 1-click show once for the window in a dock (status line). */}
      {!inDock && (compact ? <ReduceOnlyNotice /> : <SimBadge compact />)}
      {!inDock && <OneClickToggle compact={compact} />}
      {padQty === undefined && <QtyStepper value={ownQty} onChange={setOwnQty} compact />}
      {padQty === undefined && <QtyLimitBadge accountId={e.accountId} qty={ownQty} />}
      {/* Auto-centre is a setting: the module "⋯" menu in a dock, inline outside one (mobile). */}
      {!inDock && <AutoCenterToggle />}
      <button
        type="button"
        className="btn btn-sm dom-center"
        onClick={recenter}
        disabled={anchor === null}
        title={`Centre on the last price (Space)${autoCenter ? " · auto-centre is on (module ⋯ menu)" : ""}`}
      >
        <Icon name="center" size={12} /> Center
      </button>
    </div>
  );

  return (
    <div
      className={compact ? "dom dom-compact" : "dom"}
      data-trading-scope="dom"
      data-trading-ctx={ctxId}
      data-dom-live={cellGate.ok ? "true" : "false"}
      data-dom-top={top ?? undefined}
      data-dom-rows={count}
      tabIndex={0}
      aria-label={`DOM ladder ${symbol ?? ""}`}
      onKeyDown={(ev) => {
        if (ev.target !== ev.currentTarget) return;
        if (ev.code === "Space") {
          ev.preventDefault();
          recenter();
        } else if (ev.key === "PageUp" || ev.key === "PageDown") {
          ev.preventDefault();
          scrollBy((ev.key === "PageUp" ? -1 : 1) * Math.max(1, count - 2));
        } else if (ev.key === "ArrowUp" || ev.key === "ArrowDown") {
          ev.preventDefault();
          scrollBy(ev.key === "ArrowUp" ? -1 : 1);
        }
      }}
    >
      {header}
      {ghostStrip}
      <div className="dom-grid dom-cols dom-th">
        <span
          className={hasBuys ? "dom-cxl-head has" : "dom-cxl-head"}
          role={hasBuys ? "button" : undefined}
          tabIndex={hasBuys ? 0 : undefined}
          aria-label={hasBuys ? "My Bid: cancel all buy orders on this contract" : undefined}
          title={
            hasBuys ? "Cancel all your buy orders on this contract (Cxl Bids)" : "Your working buy orders"
          }
          onClick={() => {
            if (hasBuys) cancelSide("buy");
          }}
          onKeyDown={(ev) => {
            if (hasBuys && (ev.key === "Enter" || ev.key === " ")) {
              ev.preventDefault();
              ev.stopPropagation();
              cancelSide("buy");
            }
          }}
        >
          {hasBuys ? "Cxl ×" : "My Bid"}
        </span>
        <span aria-hidden="true">Bid</span>
        <span aria-hidden="true">Price</span>
        <span aria-hidden="true">Ask</span>
        <span
          className={hasSells ? "dom-cxl-head has" : "dom-cxl-head"}
          role={hasSells ? "button" : undefined}
          tabIndex={hasSells ? 0 : undefined}
          aria-label={hasSells ? "My Ask: cancel all sell orders on this contract" : undefined}
          title={
            hasSells
              ? "Cancel all your sell orders on this contract (Cxl Offers)"
              : "Your working sell orders"
          }
          onClick={() => {
            if (hasSells) cancelSide("sell");
          }}
          onKeyDown={(ev) => {
            if (hasSells && (ev.key === "Enter" || ev.key === " ")) {
              ev.preventDefault();
              ev.stopPropagation();
              cancelSide("sell");
            }
          }}
        >
          {hasSells ? "Cxl ×" : "My Ask"}
        </span>
        <span
          className="dom-pnl-head"
          role="button"
          tabIndex={0}
          aria-label={`Estimated P&L column in ${unit}; switch to ${unit === "dollars" ? "ticks" : "dollars"}`}
          title={`P&L if price reaches each row, in ${unit} — click for ${unit === "dollars" ? "ticks" : "dollars"}`}
          onClick={() => {
            void settingsSync.update({ dom_pnl_unit: unit === "dollars" ? "ticks" : "dollars" });
          }}
          onKeyDown={(ev) => {
            if (ev.key === "Enter" || ev.key === " ") {
              ev.preventDefault();
              ev.stopPropagation();
              void settingsSync.update({ dom_pnl_unit: unit === "dollars" ? "ticks" : "dollars" });
            }
          }}
        >
          {unit === "dollars" ? "P&L $" : "P&L tk"}
        </span>
        <span aria-hidden="true" title="Volume traded at price since this ladder opened">
          Vol
        </span>
      </div>
      {symbol === null ? (
        <div className="empty-state">
          <div className="empty-title">No symbol</div>
          <div className="muted">
            Pick a symbol in the dropdown above, or link this ladder to a chart's colour.
          </div>
        </div>
      ) : rows.length === 0 || !fmt ? (
        <div className="empty-state">
          <div className="muted" role="status">
            {mdStateLine({ status, notice: m?.notice ?? null })}
          </div>
        </div>
      ) : (
        <div className="dom-body dom-body-virtual" ref={bodyRef} role="table" aria-label="Price ladder">
          {lastOff && anchor !== null && (
            <button
              type="button"
              className={`dom-last-pill dom-last-pill-${lastOff}`}
              onClick={recenter}
              title="The last price is off screen: centre on it (Space)"
            >
              {lastOff === "up" ? "▲" : "▼"} Last {formatTicks(anchor, fmt)} · Center
            </button>
          )}
          {rows.map((px) => {
            const isBid = px === m?.bidTicks;
            const isAsk = px === m?.askTicks;
            const vol = m?.volumeAtPrice.get(px) ?? 0;
            const gr = ghosts?.get(px);
            return (
              <DomRow
                key={px}
                px={px}
                fmt={fmt}
                last={px === m?.lastTicks}
                bidText={isBid ? String(m.bidSize ?? "") : null}
                askText={isAsk ? String(m.askSize ?? "") : null}
                bidZone={m?.bidTicks !== null && m?.bidTicks !== undefined && px <= m.bidTicks}
                askZone={m?.askTicks !== null && m?.askTicks !== undefined && px >= m.askTicks}
                vol={vol}
                volPct={vol > 0 ? (vol / Math.max(1, maxVol)) * 100 : 0}
                position={pos?.position ?? null}
                tickValue={tickValue}
                unit={unit}
                own={mine.get(px)}
                drop={drag !== null && drag.over === px}
                dragFrom={drag !== null && drag.from === px ? drag.side : null}
                ghostKind={gr?.kind ?? null}
                ghostLabel={gr?.label ?? null}
                ghostCol={gr?.col ?? null}
                clickTip={clickTip}
                cancelBlock={cancelBlock}
                actions={actions}
              />
            );
          })}
        </div>
      )}
      {menu && fmt && (
        <OrderMenuPopup
          menu={menu}
          fmt={fmt}
          onClose={() => {
            setMenu(null);
          }}
          onCancelHere={() => {
            setMenu(null);
            cancelMine(menu.list);
          }}
          onCancelSide={(which) => {
            setMenu(null);
            cancelSide(which);
          }}
        />
      )}
    </div>
  );
}

const AUTO_CENTER_TITLE_ON =
  "Auto-centre ON: the ladder recentres when the last price leaves the middle 60 % (paused 5 s after you scroll). Click to scroll freely only.";
const AUTO_CENTER_TITLE_OFF =
  "Auto-centre OFF: the ladder stays where you scrolled it. Click to recentre automatically.";

/** The ladder's auto-centre switch (synced setting `dom_auto_center`), inline outside a dock. */
function AutoCenterToggle(): React.JSX.Element {
  const autoCenter = useSettings((s) => s.settings.dom_auto_center);
  return (
    <button
      type="button"
      className={autoCenter ? "btn btn-sm dom-auto on" : "btn btn-sm dom-auto"}
      role="switch"
      aria-checked={autoCenter}
      onClick={() => {
        void settingsSync.update({ dom_auto_center: !autoCenter });
      }}
      title={autoCenter ? AUTO_CENTER_TITLE_ON : AUTO_CENTER_TITLE_OFF}
    >
      Auto
    </button>
  );
}

/** "Auto-centre ladder" in the module "⋯" menu (a dock's DOM and order-pad DOM mode). */
export function AutoCenterOption(): React.JSX.Element {
  const autoCenter = useSettings((s) => s.settings.dom_auto_center);
  return (
    <label className="check-row" title={autoCenter ? AUTO_CENTER_TITLE_ON : AUTO_CENTER_TITLE_OFF}>
      <input
        type="checkbox"
        checked={autoCenter}
        onChange={(ev) => {
          void settingsSync.update({ dom_auto_center: ev.target.checked });
        }}
      />
      Auto-centre ladder
    </label>
  );
}

/** Right-click menu of My Bid / My Ask: cancel here, one side, or everything on the contract. */
function OrderMenuPopup({
  menu,
  fmt,
  onClose,
  onCancelHere,
  onCancelSide,
}: {
  menu: OrderMenu;
  fmt: PriceFormat;
  onClose: () => void;
  onCancelHere: () => void;
  onCancelSide: (which: "buy" | "sell" | "all") => void;
}): React.JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const win = menu.doc.defaultView ?? window;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>("button")?.focus();
    const down = (ev: PointerEvent): void => {
      if (ref.current?.contains(ev.target as Node)) return;
      closeRef.current();
    };
    const key = (ev: KeyboardEvent): void => {
      if (ev.key === "Escape") closeRef.current();
    };
    win.addEventListener("pointerdown", down, true);
    win.addEventListener("keydown", key);
    return () => {
      win.removeEventListener("pointerdown", down, true);
      win.removeEventListener("keydown", key);
    };
  }, [win]);
  const n = openQty(menu.list);
  const left = Math.min(menu.x, win.innerWidth - 230);
  const top = Math.min(menu.y, win.innerHeight - 140);
  return createPortal(
    <div
      ref={ref}
      className="popover dom-order-menu"
      role="menu"
      aria-label="Your orders"
      style={{ position: "fixed", left, top }}
    >
      <button type="button" role="menuitem" className="menu-item" onClick={onCancelHere}>
        Cancel {n} {menu.side} @ {formatTicks(menu.px, fmt)}
      </button>
      <button
        type="button"
        role="menuitem"
        className="menu-item"
        onClick={() => {
          onCancelSide(menu.side);
        }}
      >
        Cancel all {menu.side === "buy" ? "buy" : "sell"} orders
      </button>
      <button
        type="button"
        role="menuitem"
        className="menu-item"
        onClick={() => {
          onCancelSide("all");
        }}
      >
        Cancel all orders on this contract
      </button>
    </div>,
    menu.doc.body,
  );
}

/**
 * One ladder row (memoized): its props are what it shows, so a quote re-renders the rows whose
 * best bid / ask, zone, last or volume changed, not all of them. The P&L cell depends on the
 * position only (`useOpenPosition` keeps it stable between fills).
 */
const DomRow = memo(function DomRow({
  px,
  fmt,
  last,
  bidText,
  askText,
  bidZone,
  askZone,
  vol,
  volPct,
  position,
  tickValue,
  unit,
  own,
  drop,
  dragFrom,
  ghostKind,
  ghostLabel,
  ghostCol,
  clickTip,
  cancelBlock,
  actions,
}: {
  px: number;
  fmt: PriceFormat;
  last: boolean;
  /** Best bid / ask size text on the best row, else null. */
  bidText: string | null;
  askText: string | null;
  bidZone: boolean;
  askZone: boolean;
  vol: number;
  /** Width of the volume bar, % of the busiest price. */
  volPct: number;
  position: ExactPosition | null;
  tickValue: number;
  unit: DomPnlUnit;
  own: OwnOrders | undefined;
  drop: boolean;
  /** The side of the order being dragged from this row, else null. */
  dragFrom: TradeSide | null;
  ghostKind: GhostRow["kind"] | null;
  ghostLabel: string | null;
  ghostCol: GhostRow["col"] | null;
  clickTip: string;
  /** Why your orders cannot be cancelled now; null when they can. */
  cancelBlock: string | null;
  actions: RowActions;
}): React.JSX.Element {
  // Market-only row changes do not change its price or hypothetical position P&L.
  const { pnl, priceText, pnlTip } = useMemo(() => {
    const pnl = position ? domPnlCell(position, px, tickValue, unit) : null;
    return {
      pnl,
      priceText: formatTicks(px, fmt),
      pnlTip: pnl && position ? pnlTitle(pnl, px, fmt, position.netQty) : undefined,
    };
  }, [position, px, tickValue, unit, fmt]);
  const gr: GhostRow | null =
    ghostKind !== null && ghostLabel !== null && ghostCol !== null
      ? { kind: ghostKind, label: ghostLabel, col: ghostCol }
      : null;
  return (
    <div
      role="row"
      data-price={px}
      className={`dom-grid dom-cols dom-row${last ? " dom-last" : ""}${pnl?.isAvg ? " dom-avg" : ""}${drop ? " dom-drop" : ""}${gr ? ` dom-ghost-${gr.kind}` : ""}`}
      title={gr ? `Bracket preview: ${gr.label} at ${priceText} (not an order)` : undefined}
    >
      <MyCell
        side="buy"
        px={px}
        own={own}
        fmt={fmt}
        dragging={dragFrom === "buy"}
        cancelBlock={cancelBlock}
        actions={actions}
      />
      <span
        role="cell"
        className={`dom-bid num${bidZone ? " zone" : ""}${bidText !== null ? " best" : ""}`}
        title={clickTip}
        onClick={(ev) => {
          actions.click("bid", px, ev.shiftKey, ev);
        }}
        onContextMenu={(ev) => {
          ev.preventDefault();
          actions.click("bid", px, true, ev);
        }}
      >
        {gr?.col === "bid" && <GhostTag g={gr} />}
        {bidText ?? ""}
      </span>
      <span role="cell" className="dom-price num">
        {priceText}
      </span>
      <span
        role="cell"
        className={`dom-ask num${askZone ? " zone" : ""}${askText !== null ? " best" : ""}`}
        title={clickTip}
        onClick={(ev) => {
          actions.click("ask", px, ev.shiftKey, ev);
        }}
        onContextMenu={(ev) => {
          ev.preventDefault();
          actions.click("ask", px, true, ev);
        }}
      >
        {gr?.col === "ask" && <GhostTag g={gr} />}
        {askText ?? ""}
      </span>
      <MyCell
        side="sell"
        px={px}
        own={own}
        fmt={fmt}
        dragging={dragFrom === "sell"}
        cancelBlock={cancelBlock}
        actions={actions}
      />
      {pnl && position ? (
        <span role="cell" className={`dom-pnl num tone-${pnl.tone}`} title={pnlTip}>
          {pnl.text}
        </span>
      ) : (
        <span role="cell" className="dom-pnl num" />
      )}
      <span role="cell" className="dom-vol num">
        {vol > 0 && (
          <>
            <span className="dom-vol-bar" style={{ width: `${String(volPct)}%` }} />
            <span className="dom-vol-n">{formatVolume(vol)}</span>
          </>
        )}
      </span>
    </div>
  );
});

/** My Bid / My Ask cell: this account's working orders at the price (from OrderEvents only). */
const MyCell = memo(function MyCell({
  side,
  px,
  own,
  fmt,
  dragging,
  cancelBlock,
  actions,
}: {
  side: TradeSide;
  px: number;
  own: OwnOrders | undefined;
  fmt: PriceFormat;
  dragging: boolean;
  cancelBlock: string | null;
  actions: RowActions;
}): React.JSX.Element {
  const list = own ? (side === "buy" ? own.buy : own.sell) : [];
  const n = openQty(list);
  const tags = [...new Set(list.map(bracketTag).filter((t): t is string => t !== null))];
  const cls = `dom-my dom-my-${side === "buy" ? "bid" : "ask"}${n > 0 ? " has" : ""}${dragging ? " dragging" : ""}`;
  if (n === 0) return <span role="cell" className={cls} />;
  const pendingWhy = notActionableReason(list);
  const pending = list.some((o) => !isModifiable(o));
  const bracketTip =
    tags.length > 0 ? ` — bracket ${tags.join(", ")} (OCO with its sibling; the gateway keeps it sized)` : "";
  const tip = `${String(n)} ${side === "buy" ? "buy" : "sell"} @ ${formatTicks(px, fmt)} — ${MY_SEMANTICS}${bracketTip}${pendingWhy ? ` (${pendingWhy})` : pending ? " (a change is pending)" : ""}${cancelBlock === null ? "" : ` — ${cancelBlock}`}`;
  return (
    <span
      role="cell"
      className={`${cls}${pendingWhy ? " pending" : ""}`}
      tabIndex={0}
      title={tip}
      aria-label={tip}
      data-own-qty={n}
      data-own-state={pendingWhy ? "pending" : "working"}
      data-bracket={tags.length > 0 ? tags.join(" ") : undefined}
      onPointerDown={(ev) => {
        if (ev.button !== 0) return;
        if (cancelBlock !== null) {
          pushToast({ tone: "warn", title: "Your order", body: cancelBlock }, { ttlMs: 5000 });
          return;
        }
        actions.drag(side, list, px, ev);
      }}
      onContextMenu={(ev) => {
        ev.preventDefault();
        ev.stopPropagation();
        actions.menu({ x: ev.clientX, y: ev.clientY, doc: ev.currentTarget.ownerDocument, side, px, list });
      }}
      onKeyDown={(ev) => {
        if (cancelBlock !== null) return;
        if (ev.key === "Delete" || ev.key === "Backspace" || ev.key === "Enter") {
          ev.preventDefault();
          ev.stopPropagation();
          actions.cancel(list);
        } else if (ev.key === "ArrowUp" || ev.key === "ArrowDown") {
          ev.preventDefault();
          ev.stopPropagation();
          actions.move(list, px + (ev.key === "ArrowUp" ? 1 : -1));
        }
      }}
    >
      {n}
      {tags.length > 0 && (
        <span className={`dom-my-tag dom-my-tag-${tags[0]?.startsWith("SL") ? "sl" : "tp"}`}>
          {tags.join(" ")}
        </span>
      )}
      <button
        type="button"
        className="dom-my-x"
        tabIndex={-1}
        aria-label={`Cancel ${String(n)} ${side} @ ${formatTicks(px, fmt)}`}
        title="Cancel"
        onPointerDown={(ev) => {
          // The × cancels on its own: no drag, no second cancel from the cell's click.
          ev.stopPropagation();
        }}
        onClick={(ev) => {
          ev.stopPropagation();
          if (cancelBlock !== null) {
            pushToast({ tone: "warn", title: "Your order", body: cancelBlock }, { ttlMs: 5000 });
            return;
          }
          actions.cancel(list);
        }}
      >
        ×
      </button>
    </span>
  );
});
